import React, { useState, useEffect, useCallback } from 'react';
import { getVehicleBrand } from '../../utils/vehicleBrand';
import {
  Button,
  Text,
  Box,
  SimpleGrid,
  useToast,
  useDisclosure,
  Textarea,
  Input,
  Select,
  Accordion,
  Flex,
  HStack,
  Radio,
  Checkbox,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  useColorModeValue,
} from '@chakra-ui/react';
import { format, parse } from 'date-fns';
import { getData, putData, postData } from 'utils/httpClient';
import authService from 'services/authService';
import { FaPrint } from 'react-icons/fa';
import VehicleHistoryModalComplete from '../Vehicles/VehicleHistoryModalComplete';
import SchedulerServiceHero from './SchedulerServiceHero';
import SchedulerCollapsibleSection from './SchedulerCollapsibleSection';
import AvailableTimes from '../Appointment/AvailableTimes';
import { primaryButtonProps, secondaryButtonProps } from '../../theme/adminUi';
import {
  normalizeDurationMinutes,
  formatDurationLabel,
} from '../../utils/serviceDuration';
import { isWarrantyServiceType } from '../../utils/warrantyTypes';
import { canChangeServicePackageBeforeStart } from '../../utils/servicePackage';

const CALENDAR_HEIGHT = '40vh';

const DetailField = ({ label, children }) => (
  <Box>
    <Text fontSize="xs" color="gray.500" mb={1}>
      {label}
    </Text>
    {children}
  </Box>
);

const HorasDisponiblesHeader = () => {
  const lineColor = useColorModeValue('gray.400', 'gray.500');
  return (
    <Flex align="center" gap={3} py={2} flexShrink={0}>
      <Box flex={1} h="1px" bg={lineColor} />
      <Text
        fontSize="sm"
        fontWeight="bold"
        letterSpacing="wider"
        whiteSpace="nowrap"
      >
        HORAS DISPONIBLES
      </Text>
      <Box flex={1} h="1px" bg={lineColor} />
    </Flex>
  );
};

const SchedulerModalInfo = ({
  selectedEvent,
  setSelectedEvent,
  customerObservations,
  setCustomerObservations,
  mechanicObservations,
  setMechanicObservations,
  handleSaveVehicleService,
  handleSaveVehicleData,
  statusList = [],
  mechanicsList = [],
  servicesCatalog = [],
  hasAlerts = false,
  onOpenAlerts,
  showAssociateLastReception = false,
  onAssociateLastReception,
  associatingLastReception = false,
  assistant,
  setAssistant,
  onMechanicChange,
  vehicleService,
  appointmentList = [],
  holidayList = [],
  onScheduleOverlayChange,
  lockEdits = false,
}) => {
  const toast = useToast();
  const {
    isOpen: isVehicleHistoryModalOpen,
    onOpen: onVehicleHistoryModalOpen,
    onClose: onVehicleHistoryModalClose,
  } = useDisclosure();
  const [vehicleData, setVehicleData] = useState(null);
  const [loadingVehicle, setLoadingVehicle] = useState(false);
  const [isEditingKm, setIsEditingKm] = useState(false);
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [isDateSearchMode, setIsDateSearchMode] = useState(true);
  const [manualScheduleTick, setManualScheduleTick] = useState(1);
  const [accordionIndex, setAccordionIndex] = useState([]);

  const handleBlockingOverlayChange = useCallback((blocking) => {
    onScheduleOverlayChange?.(blocking);
  }, [onScheduleOverlayChange]);

  const appointment = selectedEvent?.appointment;
  const isWarrantyService = isWarrantyServiceType(
    appointment?.serviceType ||
      appointment?.serviceTypeId ||
      vehicleService?.serviceType ||
      vehicleService?.serviceTypeId,
  );
  const isMaintenanceService = Number(appointment?.serviceTypeId) === 1;
  const canChangeServicePackage =
    !lockEdits &&
    !isWarrantyService &&
    isMaintenanceService &&
    canChangeServicePackageBeforeStart(appointment?.status, {
      serviceStart: vehicleService?.serviceStart,
    });

  const handleServicePackageChange = (serviceIdRaw) => {
    const id = Number(serviceIdRaw);
    const svc = servicesCatalog.find((s) => Number(s.id) === id);
    setSelectedEvent((prev) => ({
      ...prev,
      appointment: {
        ...prev.appointment,
        serviceId: id,
        service: svc,
        description: svc?.name || prev.appointment.description,
        estimatedTime: normalizeDurationMinutes(
          svc?.estimatedTime,
          svc?.servicesEvents,
        ),
      },
    }));
  };

  useEffect(() => {
    setIsRescheduling(false);
  }, [appointment?.id]);

  const isFinished = ['Completado', 'Finalizado'].includes(appointment?.status);
  const canEditSchedule =
    !lockEdits &&
    ['Pendiente', 'Confirmado'].includes(appointment?.status);
  const canEditStaff = authService.isAdmin() && !lockEdits;
  const sortedEventHistories = [
    ...(vehicleService?.vehicleServiceEventsHistories || []),
  ].sort((a, b) =>
    String(a?.event?.code ?? '').localeCompare(String(b?.event?.code ?? ''), 'es', {
      numeric: true,
      sensitivity: 'base',
    }),
  );

  const usedProducts = vehicleService?.usedProducts || [];

  useEffect(() => {
    setAccordionIndex(canEditSchedule ? [0] : []);
  }, [appointment?.id, canEditSchedule]);
  const isConfirmado = appointment?.status === 'Confirmado';
  const showScheduleCalendar = canEditSchedule && isRescheduling;

  const scheduleDuration = normalizeDurationMinutes(
    appointment?.estimatedTime,
    appointment?.service?.servicesEvents,
  );

  const fetchVehicleData = async (vehicleId) => {
    try {
      setLoadingVehicle(true);
      const response = await getData(`/vehicles/${vehicleId}`, {
        skipLoader: true,
      });
      setVehicleData(response?.payload?.vehicle || null);
      onVehicleHistoryModalOpen();
    } catch {
      toast({
        title: 'Error al obtener datos del vehículo',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingVehicle(false);
    }
  };

  const handleOpenPdf = async (documentId) => {
    if (!documentId) return;
    try {
      const response = await getData('/document/' + documentId, {
        skipLoader: true,
      });
      const document = response.payload.document;
      if (document?.file) {
        const pdfData = new Uint8Array(document.file.data);
        const blob = new Blob([pdfData], { type: 'application/pdf' });
        window.open(URL.createObjectURL(blob), '_blank');
      }
    } catch {
      toast({
        title: 'No se pudo abrir el documento',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handlePrintService = async () => {
    const serviceId =
      appointment?.vehicleService?.id || vehicleService?.id;
    if (!serviceId) {
      toast({
        title: 'Documento no disponible',
        description: 'No se encontró el servicio para imprimir.',
        status: 'warning',
        duration: 3500,
        isClosable: true,
      });
      return;
    }
    try {
      const response = await postData(
        `/vehicleServices/${serviceId}/service-pdf`,
        {},
        { skipLoader: true },
      );
      const docId =
        response?.payload?.documentId ||
        response?.payload?.vehicleService?.documentId;
      if (!docId) {
        throw new Error('No se obtuvo el documento generado.');
      }
      await handleOpenPdf(docId);
    } catch (err) {
      toast({
        title: 'No se pudo imprimir',
        description:
          err?.response?.data?.message ||
          err?.message ||
          'Intente nuevamente.',
        status: 'error',
        duration: 4500,
        isClosable: true,
      });
    }
  };

  const recomputeEventBounds = (appt) => {
    if (!appt?.date || !appt?.time) return {};
    try {
      const start = parse(
        `${String(appt.date).slice(0, 10)} ${String(appt.time).slice(0, 5)}`,
        'yyyy-MM-dd HH:mm',
        new Date(),
      );
      if (Number.isNaN(start.getTime())) return {};
      const mins = normalizeDurationMinutes(
        appt.estimatedTime,
        appt.service?.servicesEvents,
      );
      return {
        start,
        end: new Date(start.getTime() + mins * 60 * 1000),
      };
    } catch {
      return {};
    }
  };

  const handleScheduleFieldChange = (field, value) => {
    setSelectedEvent((prev) => {
      const appointmentNext = { ...prev.appointment, [field]: value };
      return {
        ...prev,
        appointment: appointmentNext,
        ...recomputeEventBounds(appointmentNext),
      };
    });
  };

  const saveAgendaChanges = async () => {
    await putData(`/agenda/${appointment.id}`, {
      id: appointment.id,
      date: appointment.date,
      time: appointment.time,
      status: appointment.status,
      mechanicId: appointment.mechanicId,
      serviceId: appointment.serviceId,
      description: appointment.description,
      estimatedTime: appointment.estimatedTime,
    });
    if (handleSaveVehicleService && appointment?.status) {
      await handleSaveVehicleService(appointment.status, { silent: true });
    }
    toast({ title: 'Agenda actualizada', status: 'success', duration: 2500 });
    setIsRescheduling(false);
  };

  if (!selectedEvent) return null;

  const vehicle = appointment?.vehicle;
  const customer = appointment?.customer;

  return (
    <>
      <SchedulerServiceHero
        appointment={appointment}
        vehicleService={vehicleService}
        start={selectedEvent.start}
        end={selectedEvent.end}
        onOpenHistory={() => fetchVehicleData(vehicle?.id)}
        onOpenAlerts={onOpenAlerts}
        hasAlerts={hasAlerts}
        loadingHistory={loadingVehicle}
        showAssociateLastReception={showAssociateLastReception && !lockEdits}
        onAssociateLastReception={onAssociateLastReception}
        associatingLastReception={associatingLastReception}
        assistant={assistant}
        setAssistant={setAssistant}
        mechanicsList={mechanicsList}
        mechanicId={appointment?.mechanicId || vehicleService?.mechanicId}
        onMechanicChange={onMechanicChange || ((id, mechanic) => {
          setSelectedEvent((prev) => ({
            ...prev,
            appointment: {
              ...prev.appointment,
              mechanicId: id,
              mechanic,
            },
          }));
        })}
        showStaffEditor={canEditStaff}
        assistantName={
          mechanicsList?.find((m) => Number(m.id) === Number(assistant))
            ?.name || vehicleService?.assistant?.name
        }
      />

      {/* Petición del cliente — siempre visible */}
      <Box mb={4}>
        <Text fontSize="sm" fontWeight="semibold" mb={2} color="gray.700">
          Petición del cliente
        </Text>
        <Textarea
          value={customerObservations}
          onChange={(e) => !lockEdits && setCustomerObservations(e.target.value)}
          placeholder="Motivo de visita, pedidos especiales…"
          size="sm"
          minH="72px"
          resize="vertical"
          isReadOnly={lockEdits}
          bg={lockEdits ? 'gray.50' : 'white'}
        />
        {isWarrantyService && (
          <>
            <Text fontSize="sm" fontWeight="semibold" mb={2} mt={4} color="gray.700">
              Observaciones finales
            </Text>
            <Textarea
              value={mechanicObservations || ''}
              onChange={(e) =>
                !lockEdits && setMechanicObservations?.(e.target.value)
              }
              placeholder="Observaciones finales del servicio…"
              size="sm"
              minH="72px"
              resize="vertical"
              isReadOnly={lockEdits}
              bg={lockEdits ? 'gray.50' : 'white'}
            />
          </>
        )}
        {!lockEdits && (
          <Button
            {...primaryButtonProps}
            mt={2}
            onClick={() => handleSaveVehicleService(appointment?.status)}
          >
            Guardar cambios
          </Button>
        )}
      </Box>

      <Accordion
        allowMultiple
        index={accordionIndex}
        onChange={setAccordionIndex}
        reduceMotion
      >
        {isFinished && (
          <SchedulerCollapsibleSection title="Productos utilizados">
            {usedProducts.length > 0 ? (
              <Table variant="simple" size="sm" w="auto">
                <Thead>
                  <Tr>
                    <Th>Código</Th>
                    <Th>Descripción</Th>
                    <Th isNumeric>Cantidad</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {usedProducts.map((product) => (
                    <Tr key={product.id || product.productId}>
                      <Td fontSize="sm">{product.product?.code || '—'}</Td>
                      <Td fontSize="sm">
                        {product.productName || product.product?.name || '—'}
                      </Td>
                      <Td fontSize="sm" isNumeric>
                        {Number(product.quantity) || 1}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            ) : (
              <Text fontSize="sm" color="gray.500">
                —
              </Text>
            )}
          </SchedulerCollapsibleSection>
        )}
        {isFinished && !isWarrantyService && sortedEventHistories.length > 0 && (
          <SchedulerCollapsibleSection
            title="Checklist de tareas"
          >
            <TableContainer maxH="40vh" overflowY="auto">
              <Table variant="simple" size="sm">
                <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
                  <Tr>
                    <Th w="60px">Cód.</Th>
                    <Th>Descripción</Th>
                    <Th w="160px">Estado</Th>
                    <Th w="90px">Reparado</Th>
                    <Th>Nota</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {sortedEventHistories.map((serviceEvent) => {
                    const statusValue =
                      serviceEvent.status === 'Ok' || serviceEvent.status === 'NoOk'
                        ? serviceEvent.status
                        : serviceEvent.completed
                          ? 'Ok'
                          : '';
                    const isOk = statusValue === 'Ok';
                    const isNoOk = statusValue === 'NoOk';
                    const comment = serviceEvent.comment;
                    const note =
                      comment &&
                      comment !== 0 &&
                      String(comment).trim() !== '0' &&
                      String(comment).trim() !== ''
                        ? String(comment)
                        : '';

                    return (
                      <Tr
                        key={serviceEvent.id}
                        bg={isOk ? 'green.50' : isNoOk ? 'red.50' : undefined}
                      >
                        <Td fontSize="xs" fontWeight="bold" color="gray.500">
                          {serviceEvent.event?.code || '—'}
                        </Td>
                        <Td fontSize="sm" fontWeight="medium" color="gray.800">
                          {serviceEvent.eventName || serviceEvent.event?.name || '—'}
                        </Td>
                        <Td>
                          <HStack spacing={3}>
                            <Radio
                              size="sm"
                              colorScheme="green"
                              isChecked={isOk}
                              isDisabled
                            >
                              OK
                            </Radio>
                            <Radio
                              size="sm"
                              colorScheme="red"
                              isChecked={isNoOk}
                              isDisabled
                            >
                              nOk
                            </Radio>
                          </HStack>
                        </Td>
                        <Td>
                          <Checkbox
                            size="sm"
                            colorScheme="blue"
                            isChecked={!!serviceEvent.reparado}
                            isDisabled
                          >
                            Sí
                          </Checkbox>
                        </Td>
                        <Td fontSize="sm" color="gray.700">
                          {serviceEvent.event?.hasComment ? note || '—' : null}
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </TableContainer>
          </SchedulerCollapsibleSection>
        )}
        {canEditSchedule && (
          <SchedulerCollapsibleSection title="Agenda y asignación">
            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3} mb={3}>
              <DetailField label="Fecha">
                <Input
                  size="sm"
                  type="date"
                  value={appointment?.date || ''}
                  onChange={(e) => {
                    handleScheduleFieldChange('date', e.target.value);
                    setManualScheduleTick((n) => n + 1);
                  }}
                />
              </DetailField>
              <DetailField label="Hora">
                <Input
                  size="sm"
                  type="time"
                  value={appointment?.time?.slice(0, 5) || ''}
                  onChange={(e) => {
                    handleScheduleFieldChange('time', e.target.value);
                    setManualScheduleTick((n) => n + 1);
                  }}
                />
              </DetailField>
              <DetailField label="Estado">
                <Select
                  size="sm"
                  value={appointment?.status}
                  onChange={(e) =>
                    setSelectedEvent((prev) => ({
                      ...prev,
                      appointment: {
                        ...prev.appointment,
                        status: e.target.value,
                      },
                    }))
                  }
                >
                  {statusList.map((s) => (
                    <option key={s.id} value={s.name}>
                      {s.name}
                    </option>
                  ))}
                </Select>
              </DetailField>
              <DetailField label="Mecánico">
                {canEditStaff ? (
                <Select
                  size="sm"
                  value={
                    appointment?.mechanicId
                      ? String(appointment.mechanicId)
                      : ''
                  }
                  onChange={(e) => {
                    const id = Number(e.target.value);
                    const mechanic =
                      mechanicsList.find((m) => Number(m.id) === id) || null;
                    if (onMechanicChange) {
                      onMechanicChange(id, mechanic);
                    } else {
                      setSelectedEvent((prev) => ({
                        ...prev,
                        appointment: {
                          ...prev.appointment,
                          mechanicId: id,
                          mechanic,
                        },
                      }));
                    }
                    if (setAssistant && Number(assistant) === id) {
                      setAssistant('0');
                    }
                  }}
                >
                  {mechanicsList.map((m) => (
                    <option key={m.id} value={String(m.id)}>
                      {m.name}
                    </option>
                  ))}
                </Select>
                ) : (
                  <Text fontSize="sm" fontWeight="medium">
                    {appointment?.mechanic?.name ||
                      mechanicsList.find(
                        (m) =>
                          Number(m.id) ===
                          Number(appointment?.mechanicId || vehicleService?.mechanicId),
                      )?.name ||
                      '—'}
                  </Text>
                )}
              </DetailField>
              <DetailField label="Ayudante">
                {setAssistant && canEditStaff ? (
                  <Select
                    size="sm"
                    value={String(assistant ?? 0)}
                    onChange={(e) => setAssistant(e.target.value)}
                  >
                    <option value="0">Sin ayudante</option>
                    {mechanicsList
                      .filter(
                        (m) =>
                          Number(m.id) !==
                          Number(
                            appointment?.mechanicId ||
                              vehicleService?.mechanicId,
                          ),
                      )
                      .map((m) => (
                        <option key={m.id} value={String(m.id)}>
                          {m.name}
                        </option>
                      ))}
                  </Select>
                ) : (
                  <Text fontSize="sm" fontWeight="medium">
                    {mechanicsList?.find(
                      (m) => Number(m.id) === Number(assistant),
                    )?.name ||
                      vehicleService?.assistant?.name ||
                      '—'}
                  </Text>
                )}
              </DetailField>
              {canChangeServicePackage && (
                <DetailField label="Servicio (S1, S2…)">
                  <Select
                    size="sm"
                    value={appointment?.serviceId || ''}
                    onChange={(e) => handleServicePackageChange(e.target.value)}
                  >
                    {servicesCatalog.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </Select>
                </DetailField>
              )}
              <DetailField label="Duración">
                <Text fontSize="sm" fontWeight="medium" mt={1}>
                  {formatDurationLabel(scheduleDuration)}
                </Text>
              </DetailField>
              {isRescheduling && (
                <DetailField label="Modo de búsqueda">
                  <Button
                    size="sm"
                    colorScheme="gray"
                    w="100%"
                    onClick={() => setIsDateSearchMode((v) => !v)}
                  >
                    {isDateSearchMode
                      ? 'Horario recomendado'
                      : 'Buscar por fecha'}
                  </Button>
                </DetailField>
              )}
            </SimpleGrid>

            {!isRescheduling && (
              <Button
                {...secondaryButtonProps}
                mb={3}
                onClick={() => {
                  setIsRescheduling(true);
                  setManualScheduleTick((n) => n + 1);
                }}
              >
                Reagendar cita
              </Button>
            )}

            {showScheduleCalendar && appointment?.mechanicId && (
              <Box mb={3}>
                <HorasDisponiblesHeader />
                <Box
                  h={CALENDAR_HEIGHT}
                  maxH={CALENDAR_HEIGHT}
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  overflow="hidden"
                  bg="white"
                  sx={{
                    '.rbc-calendar': {
                      height: CALENDAR_HEIGHT,
                      fontSize: '11px',
                    },
                    '.rbc-toolbar': {
                      mb: 0,
                      minHeight: '28px',
                      flexWrap: 'wrap',
                    },
                    '.rbc-toolbar button': {
                      fontSize: '11px',
                      padding: '2px 6px',
                    },
                    '.rbc-time-view': { flex: 1, minHeight: 0 },
                    '.rbc-time-content': { overflowY: 'auto' },
                    '.rbc-slot-selection': {
                      backgroundColor: 'rgba(255, 235, 59, 0.35) !important',
                    },
                    '.rbc-event': { fontSize: '10px' },
                  }}
                >
                  <AvailableTimes
                    selectedDateString={appointment?.date || ''}
                    selectedTimeString={appointment?.time || ''}
                    duration={scheduleDuration || 30}
                    serviceEvents={appointment?.service?.servicesEvents}
                    searchByDate={isDateSearchMode}
                    show
                    appointmentList={appointmentList}
                    handleServiceChange={handleScheduleFieldChange}
                    holidayList={holidayList}
                    selectedMechanicId={appointment?.mechanicId}
                    excludeAppointmentId={appointment?.id}
                    disableAutoSelect
                    manualScheduleTick={manualScheduleTick}
                    calendarHeight={CALENDAR_HEIGHT}
                    onBlockingOverlayChange={handleBlockingOverlayChange}
                  />
                </Box>
              </Box>
            )}

            {showScheduleCalendar && !appointment?.mechanicId && (
              <Text fontSize="sm" color="gray.500" mb={3}>
                Seleccioná un mecánico para ver el calendario de horarios.
              </Text>
            )}

            <HStack spacing={2} flexWrap="wrap">
              {isRescheduling && (
                <Button
                  {...secondaryButtonProps}
                  onClick={() => setIsRescheduling(false)}
                >
                  Cancelar reagendo
                </Button>
              )}
              <Button {...primaryButtonProps} onClick={saveAgendaChanges}>
                Guardar cambios de agenda
              </Button>
            </HStack>
          </SchedulerCollapsibleSection>
        )}

        {canChangeServicePackage && !canEditSchedule && (
          <SchedulerCollapsibleSection title="Paquete de servicio">
            <Text fontSize="xs" color="gray.600" mb={2}>
              Podés ajustar S1/S2 hasta iniciar el servicio en taller.
            </Text>
            <DetailField label="Servicio (S1, S2…)">
              <Select
                size="sm"
                maxW={{ base: '100%', md: '320px' }}
                value={appointment?.serviceId || ''}
                onChange={(e) => handleServicePackageChange(e.target.value)}
              >
                {servicesCatalog.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            </DetailField>
            <Button {...primaryButtonProps} mt={3} onClick={saveAgendaChanges}>
              Guardar paquete
            </Button>
          </SchedulerCollapsibleSection>
        )}

        <SchedulerCollapsibleSection title="Vehículo">
          <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={4}>
            <DetailField label="Patente">
              <Text fontSize="sm" fontWeight="medium">
                {vehicle?.plate || '—'}
              </Text>
            </DetailField>
            <DetailField label="Marca / Modelo">
              <Text fontSize="sm" fontWeight="medium">
                {[getVehicleBrand(vehicle), vehicle?.model?.description]
                  .filter(Boolean)
                  .join(' ') || '—'}
              </Text>
            </DetailField>
            <DetailField label="Año">
              <Text fontSize="sm" fontWeight="medium">
                {vehicle?.year || '—'}
              </Text>
            </DetailField>
            <DetailField label="Kilómetros">
              {isConfirmado && !lockEdits ? (
                <Input
                  size="sm"
                  type="number"
                  value={vehicle?.kilometers || ''}
                  onChange={(e) => {
                    setIsEditingKm(true);
                    setSelectedEvent((prev) => ({
                      ...prev,
                      appointment: {
                        ...prev.appointment,
                        vehicle: {
                          ...prev.appointment.vehicle,
                          kilometers: e.target.value,
                        },
                      },
                    }));
                  }}
                />
              ) : (
                <Text fontSize="sm" fontWeight="medium">
                  {vehicle?.kilometers ?? '—'}
                </Text>
              )}
            </DetailField>
            <DetailField label="Chasis">
              <Text fontSize="sm" fontWeight="medium">
                {vehicle?.chassisNumber || '—'}
              </Text>
            </DetailField>
            <DetailField label="Motor">
              <Text fontSize="sm" fontWeight="medium">
                {vehicle?.engineNumber || '—'}
              </Text>
            </DetailField>
            <DetailField label="Combustible">
              <Text fontSize="sm" fontWeight="medium">
                {vehicle?.fuelType?.name || vehicle?.fuelType?.type || '—'}
              </Text>
            </DetailField>
          </SimpleGrid>
          {isConfirmado && isEditingKm && !lockEdits && (
            <Button
              {...primaryButtonProps}
              mt={3}
              onClick={async () => {
                await handleSaveVehicleData(appointment.vehicle);
                setIsEditingKm(false);
                toast({
                  title: 'Kilómetros actualizados',
                  status: 'success',
                  duration: 3000,
                });
              }}
            >
              Guardar kilómetros
            </Button>
          )}
        </SchedulerCollapsibleSection>

        <SchedulerCollapsibleSection title="Cliente">
          <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={4}>
            <DetailField label="Nombre">
              <Text fontSize="sm" fontWeight="medium">
                {customer?.name || '—'}
              </Text>
            </DetailField>
            <DetailField label="Documento">
              <Text fontSize="sm" fontWeight="medium">
                {customer?.document || '—'}
              </Text>
            </DetailField>
            <DetailField label="Teléfono">
              <Text fontSize="sm" fontWeight="medium">
                {customer?.phone || '—'}
              </Text>
            </DetailField>
          </SimpleGrid>
        </SchedulerCollapsibleSection>

        <SchedulerCollapsibleSection title="Más datos del vehículo">
            <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={4}>
              <DetailField label="Fecha de venta">
                <Text fontSize="sm">
                  {vehicle?.saleDate
                    ? format(new Date(vehicle.saleDate), 'dd/MM/yyyy')
                    : '—'}
                </Text>
              </DetailField>
            </SimpleGrid>
        </SchedulerCollapsibleSection>
      </Accordion>

      {['Completado', 'Finalizado'].includes(appointment?.status) &&
        (appointment?.vehicleService?.id || vehicleService?.id) && (
          <Button
            {...secondaryButtonProps}
            mt={4}
            leftIcon={<FaPrint />}
            onClick={handlePrintService}
          >
            Imprimir servicio
          </Button>
        )}

      <VehicleHistoryModalComplete
        isOpen={isVehicleHistoryModalOpen}
        onClose={onVehicleHistoryModalClose}
        vehicleData={vehicleData}
        isLoading={loadingVehicle}
      />
    </>
  );
};

export default SchedulerModalInfo;
